// The bodies of the Create page's generation requests, built from its settings (plan step F14;
// promptchan-backend docs/architecture/create-page.md). Pure functions: the page's state goes in, the JSON it
// sends comes out, field for field and in the same order as when the page built them inline. Unit-tested in
// tests/create-requests.test.mjs; what the backend does with each field is its business, not this file's.
import {
  isLtx,
  normalizeAspectForVersion,
} from '../../../helpers/videoVersions.js'

/** A slider's value as a number, or null when unset (the twin of helpers.jsx's toSliderNumber). */
const sliderNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isNaN(num) ? null : num
}

type Plan = string | null | undefined

/** The models Face Enhance is never sent for, whatever the toggle says. */
const NO_RESTORE_FACES = ['Anime', 'Hyperanime', 'Fur', 'Furtoon']

export type ImageSettings = {
  model: string
  prompt: string
  negativePrompt: string
  /** The seed sent: the one just rolled, or the locked one. */
  seed: string | number | null
  quality: string
  creativity: number
  imageSize: string
  /** How many images (the grid). */
  ntier: number
  lora: string
  lorasList: string[]
  filter: string | null
  details: number
  emotion: string
  faceFix: boolean
  chillMode: boolean
  safeImage: boolean
  watermark: boolean
  plan: Plan
  language: string | undefined
  animation: unknown
  stickMode: boolean
  folderSetting: unknown
  characterVersion: string
  charImage: string | null
  charImageIds: Record<string, string> | null | undefined
  customPoseImage: string | null
  showAttributes: boolean
  age: unknown
  weight: unknown
  breasts: unknown
  ass: unknown
  penis: unknown
  females: unknown
  males: unknown
  nsfwEnabled: boolean
  visibilityMode: string | null
  jobId: string
}

/**
 * An image request (/api/generate, or /api/generate_image for Flame), and its `meta`: the model, pose and
 * settings the feed files the result under, remembered with the job so a reload can file it the same way.
 */
export function imageRequest(s: ImageSettings) {
  // AI Character v3 sets the body itself, so the attribute sliders are not sent with it.
  const manualAttributesEnabled =
    s.showAttributes && !(s.charImage && s.characterVersion === 'v3')
  const attribute = <V>(value: V) => (manualAttributesEnabled ? value : null)

  const meta = {
    model: s.model,
    pose: s.lorasList?.length ? s.lorasList[0] : s.lora,
    clone: {
      model: s.model,
      prompt: s.prompt,
      negativePrompt: s.negativePrompt,
      loras: s.lora,
      lorasList: s.lorasList,
      filter: s.filter,
      creativity: s.creativity,
      details: s.details,
      emotion: s.emotion,
      imageSize: s.imageSize,
      // The seed actually sent. It is always concrete: an unlocked seed is rolled in the browser
      // (randomizeSeed), never left for the backend to pick.
      seed: s.seed,
      age_slider: attribute(s.age),
      weight_slider: attribute(s.weight),
      breast_slider: attribute(s.breasts),
      ass_slider: attribute(s.ass),
      penis_slider: attribute(s.penis),
    },
  }

  const body = {
    metadata: {
      model: s.model,
      loras: s.lora,
      loras_list: s.lorasList,
      // Casual Generations exist only at Ultra, and never for Flame.
      chillMode:
        s.quality !== 'Ultra' || s.model.includes('Flame')
          ? false
          : s.chillMode,
      safeImage: s.safeImage,
      watermarkEnabled: s.watermark,
      userPlan: s.plan ? s.plan : 'Free',
      style_filter: s.filter ? s.filter : 'Default',
      language: s.language,
      details: s.details,
      detail_slider: s.details,
      animation: s.animation,
      stickMode: s.stickMode,
      folderSetting: s.folderSetting,
      expression: s.emotion,
      charVersion: s.characterVersion,
      age_slider: attribute(sliderNumber(s.age)),
      n_female: attribute(s.females),
      n_male: attribute(s.males),
      weight_slider: attribute(sliderNumber(s.weight)),
      breast_slider: attribute(sliderNumber(s.breasts)),
      ass_slider: attribute(sliderNumber(s.ass)),
      penis_slider: attribute(sliderNumber(s.penis)),
      pixel_boost: '512x512',
      nsfw_toggle: s.nsfwEnabled,
      visibility_mode: s.visibilityMode ? s.visibilityMode : null,
    },
    payload: {
      prompt: s.prompt,
      seed: s.seed || 0,
      quality: s.quality,
      creativity: s.creativity,
      imageSize: s.imageSize,
      negative_prompt: s.negativePrompt,
      batch_size: s.ntier,
      restore_faces: NO_RESTORE_FACES.includes(s.model) ? false : s.faceFix,
      control_image:
        s.customPoseImage && s.lora === 'Custom' ? s.customPoseImage : null,
      character_image: s.charImage ? s.charImage : null,
      // The document the character came from, when known: saved on the result, so its page can link
      // back to the character.
      character_image_id: s.charImage
        ? (s.charImageIds?.[s.charImage] ?? null)
        : null,
    },
    // This render's name (src/lib/image-jobs.js): if the request is lost, the page collects the answer by
    // it. The backend ignores it unless its switch covers the person.
    job_id: s.jobId,
  }

  return { body, meta }
}

/** A tweak (/api/tweak_image) of a feed image: by document id, as base64, or by URL. */
export function tweakRequest({
  prompt,
  source,
  visibilityMode,
  folderSetting,
  language,
}: {
  prompt: string
  source: { url: string; isUrl: boolean; docRef?: string | null }
  visibilityMode: string | null
  folderSetting: unknown
  language: string | undefined
}) {
  const body: Record<string, unknown> = {
    prompt: prompt.trim(),
    visibility_mode: visibilityMode ? visibilityMode : 'Offline',
    folder_setting: folderSetting,
    language,
  }
  // A document id when there is one (the backend re-signs the stored file); else the base64 of an
  // unsaved result (checked by its hash); else the URL (checked against the allowed origins).
  if (source.isUrl && source.docRef) {
    body.image_id = source.docRef
  } else if (!source.isUrl) {
    body.image_base64 = source.url
  } else {
    body.image_url = source.url
  }
  return body
}

export type ExtendChoice = {
  extendVideoId: string
  extendMode: 'continue' | 'change'
  /** null when continuing */
  pose: string | null
  motionPrompt?: string | null
  dialogue?: string | null
  dialogueSpeaker?: string | null
  dialogueTone?: string | null
  dialogueAccent?: string | null
  /** Deliver the extension as its own clip (flag extend_clips); the backend decides, and answers `as_clip`. */
  asClip?: boolean
}

/**
 * An extend of an existing video (/api/video_v3.1/submit). The backend takes the aspect, upscale and pose
 * from the source video, so only the choice is sent.
 */
export function extendRequest(
  choice: ExtendChoice,
  {
    plan,
    folderSetting,
    safeImage,
    visibilityMode,
  }: {
    plan: Plan
    folderSetting: unknown
    safeImage: boolean
    visibilityMode: string | null
  },
) {
  return {
    extend: true,
    extend_video_id: choice.extendVideoId,
    extend_mode: choice.extendMode,
    pose: choice.pose,
    motion_prompt: choice.motionPrompt || null,
    // v5 only, and per extend: the source clip's line is never reused (it would be spoken again across the
    // join), so an extension is silent unless the person writes a new one.
    dialogue: choice.dialogue || null,
    dialogue_speaker: choice.dialogue ? choice.dialogueSpeaker : null,
    dialogue_tone: choice.dialogue ? choice.dialogueTone : null,
    dialogue_accent: choice.dialogue ? choice.dialogueAccent : null,
    userPlan: plan ? plan : 'Free',
    folderSetting,
    safeImage,
    visibility_mode: visibilityMode ? visibilityMode : null,
    // Only when asked, so every other extend is sent exactly as before.
    ...(choice.asClip ? { as_clip: true } : {}),
  }
}

export type VideoSettings = {
  prompt: string
  /** The seed sent: the one just rolled, or the locked one. */
  seed: string | number | null
  plan: Plan
  folderSetting: unknown
  safeImage: boolean
  /** The video's model and pose (text to video). */
  model: string
  lora: string
  /** An AI Character's face (text to video). */
  charImage: string | null
  /** The picture each AI Character address came from, when the page registered it (as an image's `charImageIds`). */
  charImageIds?: Record<string, string> | null
  showAttributes: boolean
  age: unknown
  weight: unknown
  breasts: unknown
  ass: unknown
  /**
   * The IMAGE tab's age. The top-level age_slider has always been sent from it, while txt2VidData's comes
   * from the video tab's: kept as it is (docs/architecture/create-page.md, "Odd things").
   */
  imageAge: unknown
  frameRate: number | null | undefined
  loop: string
  /** The image being animated: its document id, its base64 or URL, its pose. */
  refId: string | null
  refImage: string | null
  refLora: unknown
  animateMode: unknown
  videoId: unknown
  audioEnabled: boolean
  version: string
  quality: string
  aspect: string
  dialogue: string | null
  dialogueSpeaker: unknown
  dialogueTone: string | null
  dialogueAccent: string | null
  cameraMove: string | null
  length: unknown
  fps: unknown
  resolution: unknown
  visibilityMode: string | null
}

/** A new video (/api/video_v3.1/submit): from text (txt2VidData), or animating an image. */
export function videoRequest(s: VideoSettings) {
  // An AI Character sets the body itself, so the attribute sliders are not sent with one.
  const manualAttributesEnabled = s.showAttributes && !s.charImage
  const attribute = <V>(value: V) => (manualAttributesEnabled ? value : null)
  const textToVideo = {
    prompt: s.prompt,
    seed: s.seed || 0,
    character_image: s.charImage,
    // The picture the character came from, when known (as an image's): v2 names a character by it. v1 ignores it.
    ...(s.charImage
      ? { character_image_id: s.charImageIds?.[s.charImage] ?? null }
      : {}),
    age_slider: attribute(sliderNumber(s.age)),
    weight_slider: attribute(sliderNumber(s.weight)),
    breast_slider: attribute(sliderNumber(s.breasts)),
    ass_slider: attribute(sliderNumber(s.ass)),
    model: s.model,
    lora: s.lora,
  }

  // v5 (LTX) has one fixed spec: Portrait or Wide, 24 fps, audio always on, no quality tier, upscale or
  // loop. The backend normalises those away anyway, but sending the values it will use keeps the request
  // honest, and keeps the aspect (the one field it rejects rather than normalises) inside what it accepts.
  const ltx = isLtx(s.version)
  return {
    aspect: normalizeAspectForVersion(s.aspect, s.version),
    prompt: s.prompt,
    seed: s.seed || 0,
    userPlan: s.plan ? s.plan : 'Free',
    folderSetting: s.folderSetting,
    safeImage: s.safeImage,
    // High is sent as Standard with upscale_video (plan step S17 settles its price).
    video_quality: 'Standard',
    age_slider: attribute(sliderNumber(s.imageAge)),
    frame_rate: s.frameRate ? s.frameRate : 25,
    lora: s.lora,
    loop: ltx ? false : s.loop === 'Loop',
    base_image: s.refId,
    videoRefLora: s.refLora,
    animateMode: s.animateMode,
    videoId: s.videoId,
    audioEnabled: ltx ? true : s.audioEnabled,
    txt2VidData: s.refImage ? null : textToVideo,
    video_version: s.version,
    upscale_video: ltx ? false : s.quality === 'High',
    image_base64: s.refImage ? s.refImage : null,
    visibility_mode: s.visibilityMode ? s.visibilityMode : null,
    // v5 only. Left out on v4, so the WAN request body is unchanged.
    ...(ltx
      ? {
          dialogue: s.dialogue?.trim() || null,
          dialogue_speaker: s.dialogueSpeaker,
          dialogue_tone: s.dialogueTone || null,
          dialogue_accent: s.dialogueAccent || null,
          camera_move: s.cameraMove || null,
          video_length: s.length,
          video_fps: s.fps,
          video_resolution: s.resolution,
        }
      : {}),
  }
}
